import type { ApiResponse } from '@creator/sdk/creator-sdk';
import type { Post } from '@creator/sdk/modules/upvote/upvote.model';
import { post as postApi } from '@creator/sdk/network/api-service';
import { getPost } from '@src/model/upvote/helpers';
import { getDisplayName, getUserId } from '@src/model/user/helpers';
import creatorSdk from '@src/services/creator-sdk';
import { findUserReaction } from '../posts/hooks/helpers/reactions';
import { postsAtom } from '../posts/posts';
import { jotaiStore } from '../store';

export interface ReactionApiRequestPayload {
    userId?: string;
    postId?: string;
    commentId?: string;
    reactionName: string;
    isSuperComment?: boolean;
}

export const postReaction = async (payload: ReactionApiRequestPayload) => {
    const { postId, reactionName } = payload;
    // Optimistically update the store
    if (postId) reactToPost(postId, reactionName);

    const authIdToken = await creatorSdk.accountModule.getIdToken();
    const { data } = await postApi<ApiResponse<Post>>(
        '/post/reaction',
        payload,
        {
            Authorization: `Bearer ${authIdToken}`,
            'X-Firebase-AppCheck': '',
        },
        false,
    );

    return data;
};

const reactToPost = (postId: string, newReactionName: string) => {
    const post = getPost(postId);
    if (!post) throw new Error(`[Reaction] post with id ${postId} not found`);

    const myUserId = getUserId();

    // 1. Find if the user has *any* existing reaction on this post
    const existingReactionName = findUserReaction(myUserId, postId);

    // 2. If an existing reaction is found, remove it
    if (existingReactionName) removeReactionFromPost(myUserId, postId, existingReactionName);

    // 3. Add the new reaction *unless* the user clicked the same reaction again (which means they toggled it off)
    if (existingReactionName === newReactionName) return;
    addReactionToPost(myUserId, postId, newReactionName);
};

const addReactionToPost = (userId: string, postId: string, reactionName: string) => {
    jotaiStore.set(postsAtom, draft => {
        const post = draft[postId];
        if (!post) throw new Error(`[Reaction] post with id ${postId} not found`);

        // Initialize containers
        post.reactionsCount = post.reactionsCount ?? {};
        post.reactionsUsers = post.reactionsUsers ?? {};
        post.reactionsUsernames = post.reactionsUsernames ?? {};

        // Initialize specific reaction entries & update count
        // We can combine initialization and increment for the count
        post.reactionsCount[reactionName] = (post.reactionsCount[reactionName] ?? 0) + 1;

        // Initialize arrays if needed before pushing
        post.reactionsUsers[reactionName] = post.reactionsUsers[reactionName] ?? [];
        post.reactionsUsernames[reactionName] = post.reactionsUsernames[reactionName] ?? [];

        // Add user details
        post.reactionsUsers[reactionName].push(userId);
        post.reactionsUsernames[reactionName].push(getDisplayName());
    });
};

const removeReactionFromPost = (userId: string, postId: string, reactionName: string) => {
    jotaiStore.set(postsAtom, draft => {
        const post = draft[postId];
        if (!post) throw new Error(`[Reaction] post with id ${postId} not found`);

        const index = post.reactionsUsers[reactionName].findIndex(_userId => _userId === userId);
        if (index !== -1) post.reactionsUsers[reactionName].splice(index, 1);

        const index2 = post.reactionsUsernames[reactionName].findIndex(displayName => displayName === getDisplayName());
        if (index2 !== -1) post.reactionsUsernames[reactionName].splice(index2, 1);

        post.reactionsCount[reactionName] -= 1;
    });
};
